import { endOfDay, parse, startOfDay } from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import { atomWithSearchParams } from 'jotai-location'

import { BookingStateOption } from '@/constants/bookingStates'

const bookingStateUrlAtom = atomWithSearchParams('state', '')
const bookingStateStorageAtom = atomWithStorage<BookingStateOption['value']>(
  'booking_state',
  'all',
  createJSONStorage(() => sessionStorage)
)

const bookingFromUrlAtom = atomWithSearchParams('from', '')
const bookingFromStorageAtom = atomWithStorage<string>(
  'booking_from',
  '',
  createJSONStorage(() => sessionStorage)
)

const bookingUntilUrlAtom = atomWithSearchParams('until', '')
const bookingUntilStorageAtom = atomWithStorage<string>(
  'booking_until',
  '',
  createJSONStorage(() => sessionStorage)
)

export const tenantTimezoneAtom = atom<string>('Europe/Berlin')

export const bookingStateAtom = atom(
  (get) => {
    const urlValue = get(bookingStateUrlAtom)
    const storageValue = get(bookingStateStorageAtom)
    return urlValue || storageValue
  },
  (_get, set, value: BookingStateOption['value']) => {
    set(bookingStateUrlAtom, value === 'all' ? '' : value)
    set(bookingStateStorageAtom, value)
  }
)

export const bookingFromAtom = atom(
  (get) => {
    const urlValue = get(bookingFromUrlAtom)
    const storageValue = get(bookingFromStorageAtom)
    const tenantTimezone = get(tenantTimezoneAtom)

    if (urlValue) {
      // Parse locale time string directly in tenant timezone
      try {
        const parsedDate = parse(urlValue, 'yyyy-MM-dd HH:mm:ss', new Date())
        // Convert the parsed local date to tenant timezone
        const zonedDate = fromZonedTime(parsedDate, tenantTimezone)
        return isNaN(zonedDate.getTime())
          ? startOfDay(toZonedTime(new Date(), tenantTimezone))
          : zonedDate
      } catch {
        return startOfDay(toZonedTime(new Date(), tenantTimezone))
      }
    }
    if (storageValue) {
      const parsed = new Date(storageValue)
      return isNaN(parsed.getTime()) ? startOfDay(toZonedTime(new Date(), tenantTimezone)) : parsed
    }

    return startOfDay(toZonedTime(new Date(), tenantTimezone))
  },
  (get, set, value: Date) => {
    const tenantTimezone = get(tenantTimezoneAtom)
    const localeTimeString = formatInTimeZone(value, tenantTimezone, 'yyyy-MM-dd HH:mm:ss')
    set(bookingFromUrlAtom, localeTimeString)
    set(bookingFromStorageAtom, localeTimeString)
  }
)

export const bookingUntilAtom = atom(
  (get) => {
    const urlValue = get(bookingUntilUrlAtom)
    const storageValue = get(bookingUntilStorageAtom)
    const tenantTimezone = get(tenantTimezoneAtom)

    // If we have a stored value, use it
    if (urlValue && urlValue !== 'null') {
      // Parse locale time string directly in tenant timezone
      try {
        const parsedDate = parse(urlValue, 'yyyy-MM-dd HH:mm:ss', new Date())
        // Convert the parsed local date to tenant timezone
        const zonedDate = fromZonedTime(parsedDate, tenantTimezone)
        return isNaN(zonedDate.getTime())
          ? endOfDay(toZonedTime(new Date(), tenantTimezone))
          : zonedDate
      } catch {
        return endOfDay(toZonedTime(new Date(), tenantTimezone))
      }
    }
    if (storageValue && storageValue !== 'null') {
      const parsed = new Date(storageValue)
      return isNaN(parsed.getTime()) ? endOfDay(toZonedTime(new Date(), tenantTimezone)) : parsed
    }

    // No stored value, initialize with tenant timezone
    return endOfDay(toZonedTime(new Date(), tenantTimezone))
  },
  (get, set, value: Date | null) => {
    if (value === null) {
      set(bookingUntilUrlAtom, 'null')
      set(bookingUntilStorageAtom, 'null')
      return
    }
    // Format tenant local time as readable string for URL
    const tenantTimezone = get(tenantTimezoneAtom)
    const localeTimeString = formatInTimeZone(value, tenantTimezone, 'yyyy-MM-dd HH:mm:ss')
    set(bookingUntilUrlAtom, localeTimeString)
    set(bookingUntilStorageAtom, localeTimeString)
  }
)

export const bookingQueryParamsUrlAtom = atom((get) => ({
  state: get(bookingStateAtom) as BookingStateOption['value'],
  from: get(bookingFromAtom),
  until: get(bookingUntilAtom),
}))
